
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getFirestore, doc, onSnapshot, setDoc, collection, query, orderBy, writeBatch, deleteDoc, updateDoc, addDoc, getDocs, where, type Firestore, getDoc, DocumentReference, DocumentData, deleteField, limit, startAfter, type QueryDocumentSnapshot, increment } from 'firebase/firestore';
import { initializeAuth, browserLocalPersistence, type Auth } from 'firebase/auth';
// Re-export auth functions and types
export { onAuthStateChanged, signInWithEmailAndPassword, signOut, createUserWithEmailAndPassword } from 'firebase/auth';
export type { User } from 'firebase/auth';
import type { Recipe, GeneratedProblem, Feedback, ExplanationGuidelines, BaseProblem, GenerationJob, UserSettings, Worksheet, UserStats } from '../types';
import { firebaseConfig } from '../firebaseConfig';


interface FirebaseServices {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
}

let firebaseServices: FirebaseServices | null = null;

export const isFirebaseConfigured = (): { configured: boolean } => {
    const isPlaceholder = 
        !firebaseConfig.apiKey ||
        firebaseConfig.apiKey.startsWith("여기에-") ||
        firebaseConfig.projectId.startsWith("your-");
    
    return {
        configured: !isPlaceholder,
    };
};

export const getFirebaseServices = (): FirebaseServices | null => {
    if (firebaseServices) {
        return firebaseServices;
    }

    if (!isFirebaseConfigured().configured) {
        console.error("Firebase configuration is missing. Please update firebaseConfig.ts");
        return null;
    }

    try {
        const app = initializeApp(firebaseConfig);
        const auth = initializeAuth(app, { persistence: browserLocalPersistence });
        const db = getFirestore(app);

        firebaseServices = { app, auth, db };
        console.log("Firebase has been initialized successfully.");
        return firebaseServices;
    } catch (e) {
        console.error("Firebase initialization failed with an unexpected error:", e);
        return null;
    }
};

// Helper to log Firestore errors safely (avoiding circular structure issues)
const logFirestoreError = (operation: string, error: any) => {
    const message = error instanceof Error ? error.message : String(error);
    const code = (error as any)?.code;

    // Check for missing index error (failed-precondition is typical for this)
    if (code === 'failed-precondition' || message.includes('index')) {
        console.group(`%c🚨 [${operation}] Firestore Index Required`, 'color: #ff4444; font-weight: bold; font-size: 14px;');
        console.error('This query requires a composite index.');
        console.error('Please verify the index status in your Firebase Console.');
        console.error('Often, the error message in the console contains a direct link to create it.');
        console.error('Error Code:', code);
        console.error('Message:', message);
        console.groupEnd();
    } else {
        console.error(`[${operation}] Error:`, message);
        if (code) {
            console.error(`[${operation}] Code:`, code);
        }
    }
};

// Helper to get user-specific collection reference: users/{userId}/{collectionName}
const getUserCollectionRef = (userId: string, collectionName: string) => {
    const services = getFirebaseServices();
    return services ? collection(services.db, 'users', userId, collectionName) : null;
};

// Helper to get user-specific doc reference: users/{userId}/{collectionName}/{docId}
const getUserDocRef = (userId: string, collectionName: string, docId: string) => {
    const services = getFirebaseServices();
    return services ? doc(services.db, 'users', userId, collectionName, docId) : null;
};

// --- User Settings Functions ---
// Stored in users/{userId}/config/preferences
export const getUserSettings = async (userId: string): Promise<UserSettings | null> => {
    const settingsDocRef = getUserDocRef(userId, 'config', 'preferences');
    if (!settingsDocRef) return null;
    try {
        const docSnap = await getDoc(settingsDocRef);
        if (docSnap.exists()) {
            return docSnap.data() as UserSettings;
        }
        return null;
    } catch (e) {
        logFirestoreError('getUserSettings', e);
        return null;
    }
};

export const updateUserSettings = async (userId: string, settings: Partial<UserSettings>) => {
    const settingsDocRef = getUserDocRef(userId, 'config', 'preferences');
    if (!settingsDocRef) {
        console.warn("Firestore not initialized. Cannot update user settings.");
        return;
    }
    try {
        await setDoc(settingsDocRef, settings, { merge: true });
    } catch (e) {
        logFirestoreError('updateUserSettings', e);
    }
};


export { collection, query, where, getDocs };

// --- User Stats Functions ---
// Stored in users/{userId}/stats/general
export const incrementUserStat = async (userId: string, field: keyof UserStats, amount: number = 1) => {
    const services = getFirebaseServices();
    if (!services) return;
    const statsRef = doc(services.db, 'users', userId, 'stats', 'general');
    try {
        await setDoc(statsRef, { [field]: increment(amount) }, { merge: true });
    } catch (e) {
        logFirestoreError(`incrementUserStat (${field})`, e);
    }
};

export const getUserStats = async (userId: string): Promise<UserStats | null> => {
    const services = getFirebaseServices();
    if (!services) return null;
    const statsRef = doc(services.db, 'users', userId, 'stats', 'general');
    try {
        const docSnap = await getDoc(statsRef);
        if (docSnap.exists()) {
            return docSnap.data() as UserStats;
        }
        return {
            totalProblemsGenerated: 0,
            totalProblemsImported: 0,
            explanationRegenerations: 0,
            mathFixCount: 0,
            worksheetsCreated: 0,
            creativeProblemsGenerated: 0
        };
    } catch (e) {
        logFirestoreError('getUserStats', e);
        return null;
    }
};

// --- Recipe Functions ---
// Stored in users/{userId}/config/recipe
export const subscribeToRecipe = (userId: string | undefined, callback: (recipe: Recipe) => void): (() => void) => {
    if (!userId) {
        callback({ principles: [], generalTools: [], specificTools: {} });
        return () => {};
    }
    const recipeDocRef = getUserDocRef(userId, 'config', 'recipe');
    if (!recipeDocRef) {
        callback({ principles: [], generalTools: [], specificTools: {} });
        return () => {};
    }

    const unsubscribe = onSnapshot(recipeDocRef, (docSnap) => {
        if (docSnap.exists()) {
            callback(docSnap.data() as Recipe);
        } else {
            callback({ principles: [], generalTools: [], specificTools: {} });
        }
    }, (error: any) => {
        console.error("Error subscribing to recipe:", error.message);
        callback({ principles: [], generalTools: [], specificTools: {} });
    });

    return unsubscribe;
};

export const updateRecipe = async (userId: string, recipe: Recipe) => {
    const recipeDocRef = getUserDocRef(userId, 'config', 'recipe');
    if (!recipeDocRef) return;
    try {
        await setDoc(recipeDocRef, recipe);
    } catch (e) {
        logFirestoreError('updateRecipe', e);
    }
};

// --- Explanation Guidelines Functions ---
// Stored in users/{userId}/config/explanationGuidelines
export const subscribeToExplanationGuidelines = (userId: string | undefined, callback: (guidelines: ExplanationGuidelines) => void): (() => void) => {
    if (!userId) {
        callback({ guidelines: [] });
        return () => {};
    }
    const guidelinesDocRef = getUserDocRef(userId, 'config', 'explanationGuidelines');
    if (!guidelinesDocRef) {
        callback({ guidelines: [] });
        return () => {};
    }
    const unsubscribe = onSnapshot(guidelinesDocRef, (docSnap) => {
        if (docSnap.exists()) {
            callback(docSnap.data() as ExplanationGuidelines);
        } else {
            callback({ guidelines: [] });
        }
    }, (error: any) => {
        console.error("Error subscribing to explanation guidelines:", error.message);
        callback({ guidelines: [] });
    });
    return unsubscribe;
};

export const updateExplanationGuidelines = async (userId: string, guidelines: ExplanationGuidelines) => {
    const guidelinesDocRef = getUserDocRef(userId, 'config', 'explanationGuidelines');
    if (!guidelinesDocRef) return;
    try {
        await setDoc(guidelinesDocRef, guidelines);
    } catch (e: any) {
        logFirestoreError('updateExplanationGuidelines', e);
    }
};


// --- Problem History Functions (Generated Problems) ---
// Stored in users/{userId}/problemHistory
export const fetchGeneratedProblemsPaginated = async (userId: string | undefined, pageParam: QueryDocumentSnapshot | null = null) => {
    if (!userId) return { problems: [], lastVisible: null };
    
    const historyCollectionRef = getUserCollectionRef(userId, 'problemHistory');
    if (!historyCollectionRef) throw new Error("Collection ref not available.");

    try {
        // Changed from orderBy('createdAt', 'desc') to orderBy('orderIndex', 'desc') for custom sorting
        // 'desc' is used because newer items (higher timestamp) should be at top by default.
        // Custom sort order logic also aligns with this (larger index = higher in list).
        const constraints: any[] = [
            where('deletedAt', '==', null),
            orderBy('orderIndex', 'desc'), 
            limit(50)
        ];

        if (pageParam) {
            constraints.push(startAfter(pageParam));
        }

        const q = query(historyCollectionRef, ...constraints);
        const querySnapshot = await getDocs(q);

        const problems: GeneratedProblem[] = [];
        querySnapshot.forEach((doc) => {
            problems.push({ ...(doc.data() as Omit<GeneratedProblem, 'id'>), id: doc.id });
        });
        
        return {
            problems,
            lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1],
        };
    } catch (e) {
        logFirestoreError('fetchGeneratedProblemsPaginated', e);
        return { problems: [], lastVisible: null };
    }
};

export const addGeneratedProblems = async (userId: string, problems: Omit<GeneratedProblem, 'id'>[]) => {
    const services = getFirebaseServices();
    const historyCollectionRef = getUserCollectionRef(userId, 'problemHistory');
    if (!services || !historyCollectionRef) {
        return [];
    }
    
    const CHUNK_SIZE = 5; // Safe limit per batch to prevent transparency/timeout issues
    const allIds: string[] = [];

    try {
        // Process in chunks to avoid overwhelming Firestore or the Network
        for (let i = 0; i < problems.length; i += CHUNK_SIZE) {
            const chunk = problems.slice(i, i + CHUNK_SIZE);
            const batch = writeBatch(services.db);
            const chunkIds: string[] = [];

            chunk.forEach(problem => {
                const newDocRef = doc(historyCollectionRef);
                // Initialize orderIndex with createdAt timestamp (numeric)
                const orderIndex = new Date(problem.createdAt).getTime();
                // Explicitly set deletedAt to null if undefined to ensure it appears in queries
                batch.set(newDocRef, { ...problem, orderIndex, deletedAt: null });
                chunkIds.push(newDocRef.id);
            });

            await batch.commit();
            allIds.push(...chunkIds);
        }
        
        return allIds;
    } catch (e) {
        logFirestoreError('addGeneratedProblems', e);
        // CRITICAL FIX: Do NOT return partial IDs on failure. Throw error to let the UI know the job failed.
        throw new Error("문제 저장 중 오류가 발생했습니다. 일부 데이터가 저장되지 않았습니다.");
    }
};

export const deleteGeneratedProblem = async (userId: string, problemId: string) => {
    const services = getFirebaseServices();
    if (!services) return;
    
    try {
        const problemRef = doc(services.db, 'users', userId, 'problemHistory', problemId);
        const docSnap = await getDoc(problemRef);
        if (docSnap.exists()) {
            const currentFolder = docSnap.data().folder;
            await updateDoc(problemRef, { 
                deletedAt: new Date().toISOString(),
                originalFolder: currentFolder || ''
            });
        }
    } catch (e) {
        logFirestoreError(`deleteGeneratedProblem (${problemId})`, e);
        throw e;
    }
};

export const deleteMultipleGeneratedProblems = async (userId: string, problemIds: string[]) => {
    const services = getFirebaseServices();
    if (!services) throw new Error("Firebase is not initialized.");
    const { db } = services;
    try {
        const docRefs = problemIds.map(id => doc(db, 'users', userId, 'problemHistory', id));
        const docs = await Promise.all(docRefs.map(ref => getDoc(ref)));

        const batch = writeBatch(db);
        const deletedAt = new Date().toISOString();

        docs.forEach(docSnap => {
            if (docSnap.exists()) {
                batch.update(docSnap.ref, { 
                    deletedAt, 
                    originalFolder: docSnap.data().folder || ''
                });
            }
        });

        await batch.commit();
    } catch (e) {
        logFirestoreError('deleteMultipleGeneratedProblems', e);
        throw e;
    }
};

export const updateProblemFeedback = async (userId: string, problemId: string, feedback: Feedback) => {
    const services = getFirebaseServices();
    if (!services) return;
    try {
        await updateDoc(doc(services.db, 'users', userId, 'problemHistory', problemId), { feedback });
    } catch (e) {
        logFirestoreError('updateProblemFeedback', e);
    }
};

export const updateGeneratedProblem = async (userId: string, problemId: string, data: Partial<Omit<GeneratedProblem, 'id'>>) => {
    const services = getFirebaseServices();
    if (!services) return;
    try {
        await updateDoc(doc(services.db, 'users', userId, 'problemHistory', problemId), data);
    } catch (e) {
        logFirestoreError('updateGeneratedProblem', e);
    }
};

// --- Base Problem History Functions ---
// Stored in users/{userId}/baseProblemHistory
export const fetchBaseProblemsPaginated = async (userId: string | undefined, pageParam: QueryDocumentSnapshot | null = null) => {
    if (!userId) return { problems: [], lastVisible: null };

    const baseProblemHistoryCollectionRef = getUserCollectionRef(userId, 'baseProblemHistory');
    if (!baseProblemHistoryCollectionRef) throw new Error("Collection ref not available.");

    try {
        // Changed from orderBy('createdAt', 'desc') to orderBy('orderIndex', 'desc') for custom sorting
        const constraints: any[] = [
            where('deletedAt', '==', null),
            orderBy('orderIndex', 'desc'),
            limit(50)
        ];

        if (pageParam) {
            constraints.push(startAfter(pageParam));
        }

        const q = query(baseProblemHistoryCollectionRef, ...constraints);
        const querySnapshot = await getDocs(q);

        const problems: BaseProblem[] = [];
        querySnapshot.forEach((doc) => {
            problems.push({ ...(doc.data() as Omit<BaseProblem, 'id'>), id: doc.id });
        });
        
        return {
            problems,
            lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1],
        };
    } catch (e) {
        logFirestoreError('fetchBaseProblemsPaginated', e);
        return { problems: [], lastVisible: null };
    }
};

export const addMultipleBaseProblems = async (userId: string, problems: Omit<BaseProblem, 'id' | 'duplicateOf'>[]): Promise<string[]> => {
    const services = getFirebaseServices();
    const collectionRef = getUserCollectionRef(userId, 'baseProblemHistory');
    if (!services || !collectionRef) {
        return [];
    }
    
    const CHUNK_SIZE = 5; // Safe limit per batch
    const allIds: string[] = [];

    try {
        // Process in chunks
        for (let i = 0; i < problems.length; i += CHUNK_SIZE) {
            const chunk = problems.slice(i, i + CHUNK_SIZE);
            const batch = writeBatch(services.db);
            const chunkIds: string[] = [];
            
            chunk.forEach((problem, idx) => {
                const docRef = doc(collectionRef);
                const problemData = { ...problem, deletedAt: null };
                // Ensure orderIndex is set. If not provided, use timestamp logic.
                // We use a slight offset to keep them in insertion order (descending) within the chunk
                if (problemData.orderIndex === undefined) {
                     // Use global index (i + idx) to maintain order across chunks
                     problemData.orderIndex = new Date(problemData.createdAt).getTime() - (i + idx); 
                }
                batch.set(docRef, problemData);
                chunkIds.push(docRef.id);
            });
            
            await batch.commit();
            allIds.push(...chunkIds);
        }
        return allIds;
    } catch (e) {
        logFirestoreError('addMultipleBaseProblems', e);
        throw new Error("기반 문제 저장 중 오류가 발생했습니다.");
    }
};

export const deleteBaseProblem = async (userId: string, problemId: string) => {
    const services = getFirebaseServices();
    if (!services) throw new Error("Firebase is not initialized.");
    const { db } = services;

    try {
        const batch = writeBatch(db);
        const deletedAt = new Date().toISOString();

        // 1. Soft delete the base problem.
        const baseProblemRef = doc(db, 'users', userId, 'baseProblemHistory', problemId);
        const baseProblemSnap = await getDoc(baseProblemRef);
        if (baseProblemSnap.exists()) {
            batch.update(baseProblemRef, { deletedAt, originalFolder: baseProblemSnap.data().folder || '' });
        }

        // 2. Find and soft delete all associated generated problems.
        const generatedProblemsQuery = query(collection(db, 'users', userId, 'problemHistory'), where('baseProblemId', '==', problemId));
        const generatedProblemsSnapshot = await getDocs(generatedProblemsQuery);
        
        generatedProblemsSnapshot.forEach(doc => {
            batch.update(doc.ref, { deletedAt, originalFolder: doc.data().folder || '' });
        });

        await batch.commit();
    } catch (e) {
        logFirestoreError('deleteBaseProblem', e);
        throw e;
    }
};

export const deleteMultipleBaseProblems = async (userId: string, problemIds: string[]) => {
    const services = getFirebaseServices();
    if (!services) throw new Error("Firebase is not initialized.");
    const { db } = services;
    try {
        const batch = writeBatch(db);
        const deletedAt = new Date().toISOString();

        const baseProblemDocs = await Promise.all(problemIds.map(id => getDoc(doc(db, 'users', userId, 'baseProblemHistory', id))));
        baseProblemDocs.forEach(docSnap => {
            if (docSnap.exists()) {
                batch.update(docSnap.ref, { deletedAt, originalFolder: docSnap.data().folder || '' });
            }
        });

        const associatedProblemsPromises = problemIds.map(id => {
            const generatedProblemsQuery = query(collection(db, 'users', userId, 'problemHistory'), where('baseProblemId', '==', id));
            return getDocs(generatedProblemsQuery);
        });

        const snapshots = await Promise.all(associatedProblemsPromises);

        snapshots.forEach(snapshot => {
            snapshot.forEach(doc => {
                batch.update(doc.ref, { deletedAt, originalFolder: doc.data().folder || '' });
            });
        });
        
        await batch.commit();
    } catch (e) {
        logFirestoreError('deleteMultipleBaseProblems', e);
        throw e;
    }
};

export const updateBaseProblem = async (userId: string, problemId: string, data: Partial<Omit<BaseProblem, 'id'>>) => {
    const services = getFirebaseServices();
    if (!services) return;
    try {
        await updateDoc(doc(services.db, 'users', userId, 'baseProblemHistory', problemId), data);
    } catch (e) {
        logFirestoreError('updateBaseProblem', e);
    }
};

// --- Custom Sorting Functions ---

export const updateProblemOrder = async (userId: string, problemId: string, isGenerated: boolean, newOrderIndex: number) => {
    const services = getFirebaseServices();
    if (!services) return;
    const collectionName = isGenerated ? 'problemHistory' : 'baseProblemHistory';
    try {
        await updateDoc(doc(services.db, 'users', userId, collectionName, problemId), { orderIndex: newOrderIndex });
    } catch (e) {
        logFirestoreError('updateProblemOrder', e);
    }
};

// IMPORTANT: This function must be called once to migrate existing data that lacks 'orderIndex'
export const initializeOrderIndices = async (userId: string) => {
    const services = getFirebaseServices();
    if (!services) return;
    const { db } = services;

    const baseRef = collection(db, 'users', userId, 'baseProblemHistory');
    const genRef = collection(db, 'users', userId, 'problemHistory');

    const updateCollection = async (ref: any) => {
        const snapshot = await getDocs(ref); 
        const batch = writeBatch(db);
        let count = 0;

        snapshot.forEach((doc) => {
            // Fix: Cast data to any to allow property access without strict type error
            const data = doc.data() as any; 
            if (data.orderIndex === undefined) {
                const orderIndex = new Date(data.createdAt).getTime();
                batch.update(doc.ref, { orderIndex });
                count++;
            }
        });
        
        if (count > 0) {
            await batch.commit();
        }
        return count;
    };

    try {
        const baseCount = await updateCollection(baseRef);
        const genCount = await updateCollection(genRef);
        console.log(`Initialized orderIndex for ${baseCount} base problems and ${genCount} generated problems.`);
        return baseCount + genCount;
    } catch (e) {
        logFirestoreError('initializeOrderIndices', e);
        throw e;
    }
};


// --- Folder Management Functions ---
// Stored in users/{userId}/config/folderStructure
export const subscribeToFolderStructure = (userId: string | undefined, callback: (structure: { paths: string[] }) => void): (() => void) => {
    if (!userId) {
        callback({ paths: [] });
        return () => {};
    }
    const docRef = getUserDocRef(userId, 'config', 'folderStructure');
    if (!docRef) {
        callback({ paths: [] });
        return () => {};
    }
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
            const data = docSnap.data() as { paths?: string[] };
            // Ensure paths is string[]
            const paths = Array.isArray(data.paths) ? data.paths.filter((p): p is string => typeof p === 'string') : [];
            callback({ paths });
        } else {
            callback({ paths: [] });
        }
    }, (error) => {
        console.error("Error subscribing to folder structure:", error);
        callback({ paths: [] });
    });
    return unsubscribe;
};

export const saveFolderStructure = async (userId: string, structure: { paths: string[] }) => {
    const docRef = getUserDocRef(userId, 'config', 'folderStructure');
    if (!docRef) return;
    try {
        await setDoc(docRef, structure);
    } catch (e) {
        logFirestoreError('saveFolderStructure', e);
    }
};

export const syncFolderStructure = async (userId: string) => {
    const services = getFirebaseServices();
    if (!services) throw new Error("Firebase is not initialized.");
    const { db } = services;

    try {
        // Query only folders to avoid fetching large data (although firestore doesn't support partial fetch, we must fetch docs)
        // Optimisation: We only need 'folder' field.
        const baseRef = collection(db, 'users', userId, 'baseProblemHistory');
        const genRef = collection(db, 'users', userId, 'problemHistory');

        const [baseSnapshot, genSnapshot] = await Promise.all([
            getDocs(query(baseRef, where('deletedAt', '==', null))),
            getDocs(query(genRef, where('deletedAt', '==', null)))
        ]);

        const uniqueFolders = new Set<string>();

        baseSnapshot.forEach(doc => {
            const f = doc.data().folder;
            if (f) uniqueFolders.add(f);
        });
        genSnapshot.forEach(doc => {
            const f = doc.data().folder;
            if (f) uniqueFolders.add(f);
        });

        const paths = Array.from(uniqueFolders).sort();
        await saveFolderStructure(userId, { paths });
        
        return paths.length;
    } catch (e) {
        logFirestoreError('syncFolderStructure', e);
        throw e;
    }
};

export const renameFolder = async (userId: string, collectionName: 'baseProblemHistory' | 'problemHistory', oldPath: string, newPath: string) => {
    const services = getFirebaseServices();
    if (!services) throw new Error("Firebase is not initialized.");
    const { db } = services;

    try {
        const oldPrefix = oldPath + '/';
        const newPrefix = newPath + '/';

        const exactMatchQuery = query(collection(db, 'users', userId, collectionName), where('folder', '==', oldPath));
        const subfolderQuery = query(collection(db, 'users', userId, collectionName), where('folder', '>=', oldPrefix), where('folder', '<', oldPrefix + '\uf8ff'));
        
        const [exactSnapshot, subfolderSnapshot] = await Promise.all([getDocs(exactMatchQuery), getDocs(subfolderQuery)]);
        
        if (exactSnapshot.empty && subfolderSnapshot.empty) {
            return;
        }
        
        const batch = writeBatch(db);
        
        exactSnapshot.forEach(doc => {
            batch.update(doc.ref, { folder: newPath });
        });

        subfolderSnapshot.forEach(doc => {
            const oldFolderPath = doc.data().folder;
            const newFolderPath = oldFolderPath.replace(oldPrefix, newPrefix);
            batch.update(doc.ref, { folder: newFolderPath });
        });

        await batch.commit();
    } catch (e) {
        logFirestoreError('renameFolder', e);
    }
};

export const deleteProblemsByFolderPath = async (userId: string, collectionName: 'baseProblemHistory' | 'problemHistory', path: string) => {
    const services = getFirebaseServices();
    if (!services) throw new Error("Firebase is not initialized.");
    const { db } = services;

    try {
        const prefix = path + '/';
        // Changed to remove the dependency on a composite index for 'folder' and 'deletedAt'
        const exactMatchQuery = query(collection(db, 'users', userId, collectionName), where('folder', '==', path));
        const subfolderQuery = query(collection(db, 'users', userId, collectionName), where('folder', '>=', prefix), where('folder', '<', prefix + '\uf8ff'));

        const [exactSnapshot, subfolderSnapshot] = await Promise.all([getDocs(exactMatchQuery), getDocs(subfolderQuery)]);
        
        if (exactSnapshot.empty && subfolderSnapshot.empty) return [];

        const problemIdsToDelete: string[] = [];
        
        // Filter in memory to avoid composite index error
        exactSnapshot.forEach(doc => {
            const data = doc.data();
            if (!data.deletedAt) {
                problemIdsToDelete.push(doc.id);
            }
        });
        subfolderSnapshot.forEach(doc => {
            const data = doc.data();
            if (!data.deletedAt) {
                problemIdsToDelete.push(doc.id);
            }
        });

        if (problemIdsToDelete.length === 0) return [];

        if (collectionName === 'baseProblemHistory') {
            await deleteMultipleBaseProblems(userId, problemIdsToDelete);
        } else {
            await deleteMultipleGeneratedProblems(userId, problemIdsToDelete);
        }
        return problemIdsToDelete;
    } catch (e) {
        logFirestoreError('deleteProblemsByFolderPath', e);
        return [];
    }
};

// --- Trash Functions ---
export const fetchTrashedProblemsPaginated = async (userId: string | undefined, pageParam: string | null = null) => {
    if (!userId) return { problems: [], lastVisible: null };
    
    const services = getFirebaseServices();
    if (!services) throw new Error("Firebase not initialized.");

    const PAGE_SIZE = 50;
    
    try {
        const baseQueryConstraints: any[] = [where('deletedAt', '!=', null), orderBy('deletedAt', 'desc'), limit(PAGE_SIZE)];
        const generatedQueryConstraints: any[] = [where('deletedAt', '!=', null), orderBy('deletedAt', 'desc'), limit(PAGE_SIZE)];
        
        if(pageParam) {
            baseQueryConstraints.push(startAfter(pageParam));
            generatedQueryConstraints.push(startAfter(pageParam));
        }

        const baseRef = getUserCollectionRef(userId, 'baseProblemHistory');
        const generatedRef = getUserCollectionRef(userId, 'problemHistory');
        if(!baseRef || !generatedRef) throw new Error("Collection ref not available.");
        
        const qBase = query(baseRef, ...baseQueryConstraints);
        const qGenerated = query(generatedRef, ...generatedQueryConstraints);
        
        const [baseSnapshot, generatedSnapshot] = await Promise.all([getDocs(qBase), getDocs(qGenerated)]);

        const baseResults: BaseProblem[] = baseSnapshot.docs.map(doc => ({ ...(doc.data() as Omit<BaseProblem, 'id'>), id: doc.id }));
        const generatedResults: GeneratedProblem[] = generatedSnapshot.docs.map(doc => ({ ...(doc.data() as Omit<GeneratedProblem, 'id'>), id: doc.id }));

        const combined = [...baseResults, ...generatedResults];
        combined.sort((a, b) => new Date(b.deletedAt!).getTime() - new Date(a.deletedAt!).getTime());

        const limitedResults = combined.slice(0, PAGE_SIZE);

        const lastItem = limitedResults.length > 0 ? limitedResults[limitedResults.length - 1] : null;

        return {
            problems: limitedResults,
            lastVisible: lastItem ? lastItem.deletedAt : null
        };
    } catch (e) {
        logFirestoreError('fetchTrashedProblemsPaginated', e);
        return { problems: [], lastVisible: null };
    }
};

export const restoreGeneratedProblem = async (userId: string, problemId: string) => {
    const services = getFirebaseServices();
    if (!services) return;
    try {
        const problemRef = doc(services.db, 'users', userId, 'problemHistory', problemId);
        const docSnap = await getDoc(problemRef);
        if (docSnap.exists()) {
            const data = docSnap.data();
            await updateDoc(problemRef, { 
                deletedAt: deleteField(),
                folder: data.originalFolder,
                originalFolder: deleteField()
            });
        }
    } catch (e) {
        logFirestoreError(`restoreGeneratedProblem (${problemId})`, e);
        throw e;
    }
};

export const restoreBaseProblem = async (userId: string, problemId: string) => {
    const services = getFirebaseServices();
    if (!services) throw new Error("Firebase not initialized.");
    const { db } = services;
    try {
        const batch = writeBatch(db);
        
        const baseProblemRef = doc(db, 'users', userId, 'baseProblemHistory', problemId);
        const baseProblemSnap = await getDoc(baseProblemRef);
        if (baseProblemSnap.exists()) {
            const data = baseProblemSnap.data();
            batch.update(baseProblemRef, { 
                deletedAt: deleteField(), 
                folder: data.originalFolder, 
                originalFolder: deleteField() 
            });
        }

        const generatedProblemsQuery = query(collection(db, 'users', userId, 'problemHistory'), where('baseProblemId', '==', problemId));
        const generatedProblemsSnapshot = await getDocs(generatedProblemsQuery);
        generatedProblemsSnapshot.forEach(doc => {
            const data = doc.data();
            if (data.deletedAt) {
                batch.update(doc.ref, { 
                    deletedAt: deleteField(), 
                    folder: data.originalFolder, 
                    originalFolder: deleteField() 
                });
            }
        });
    
        await batch.commit();
    } catch (e) {
        logFirestoreError(`restoreBaseProblem (${problemId})`, e);
        throw e;
    }
};

export const permanentlyDeleteGeneratedProblem = async (userId: string, problemId: string) => {
    const services = getFirebaseServices();
    if (!services) throw new Error("Firebase is not initialized.");
    const { db } = services;
    try {
        const batch = writeBatch(db);
        batch.delete(doc(db, 'users', userId, 'problemHistory', problemId));

        const jobsQuery = query(collection(db, 'users', userId, 'generationJobs'), where('generatedProblemIds', 'array-contains', problemId));
        const jobsSnapshot = await getDocs(jobsQuery);
        jobsSnapshot.forEach(doc => batch.delete(doc.ref));
        
        await batch.commit();
    } catch (e) {
        logFirestoreError(`permanentlyDeleteGeneratedProblem (${problemId})`, e);
        throw e;
    }
};

export const permanentlyDeleteBaseProblem = async (userId: string, problemId: string) => {
    const services = getFirebaseServices();
    if (!services) throw new Error("Firebase is not initialized.");
    const { db } = services;
    try {
        const batch = writeBatch(db);

        batch.delete(doc(db, 'users', userId, 'baseProblemHistory', problemId));

        const genProblemsQuery = query(collection(db, 'users', userId, 'problemHistory'), where('baseProblemId', '==', problemId));
        const genProblemsSnapshot = await getDocs(genProblemsQuery);
        genProblemsSnapshot.forEach(doc => batch.delete(doc.ref));
        
        const jobsQuery = query(collection(db, 'users', userId, 'generationJobs'), where('baseProblemId', '==', problemId));
        const jobsSnapshot = await getDocs(jobsQuery);
        jobsSnapshot.forEach(doc => batch.delete(doc.ref));

        await batch.commit();
    } catch (e) {
        logFirestoreError(`permanentlyDeleteBaseProblem (${problemId})`, e);
        throw e;
    }
};


// --- Generation Job Functions ---
// Stored in users/{userId}/generationJobs
export const subscribeToGenerationJobs = (userId: string | undefined, callback: (jobs: GenerationJob[]) => void): (() => void) => {
    if (!userId) {
        callback([]);
        return () => {};
    }
    const generationJobsCollectionRef = getUserCollectionRef(userId, 'generationJobs');
    if (!generationJobsCollectionRef) {
        callback([]);
        return () => {};
    }
    const q = query(generationJobsCollectionRef, orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const jobs: GenerationJob[] = [];
        querySnapshot.forEach((doc) => {
            jobs.push({ ...(doc.data() as Omit<GenerationJob, 'id'>), id: doc.id });
        });
        callback(jobs);
    }, (error: any) => {
        console.error("Error subscribing to generation jobs:", error);
        callback([]);
    });
    return unsubscribe;
};

export const addGenerationJob = async (userId: string, job: Omit<GenerationJob, 'id'>): Promise<string | null> => {
    const generationJobsCollectionRef = getUserCollectionRef(userId, 'generationJobs');
    if (!generationJobsCollectionRef) {
        throw new Error("Firestore가 초기화되지 않았습니다. 작업을 추가할 수 없습니다.");
    }
    try {
        const docRef = await addDoc(generationJobsCollectionRef, job);
        return docRef.id;
    } catch (e) {
        logFirestoreError('addGenerationJob', e);
        throw e;
    }
};

export const getNewGenerationJobId = (userId: string): string | null => {
    const generationJobsCollectionRef = getUserCollectionRef(userId, 'generationJobs');
    if (!generationJobsCollectionRef) return null;
    return doc(generationJobsCollectionRef).id;
};

export const createGenerationJobWithId = async (userId: string, jobId: string, job: Omit<GenerationJob, 'id'>) => {
    const jobDocRef = getUserDocRef(userId, 'generationJobs', jobId);
    if (!jobDocRef) throw new Error("Firestore not initialized.");
    await setDoc(jobDocRef, job);
};

export const updateGenerationJob = async (userId: string, jobId: string, data: Partial<Omit<GenerationJob, 'id'>>): Promise<boolean> => {
    const services = getFirebaseServices();
    if (!services) return false;
    try {
        await updateDoc(doc(services.db, 'users', userId, 'generationJobs', jobId), data);
        return true;
    } catch (e: any) {
        // Ignore "No document to update" error if the job was deleted
        if (e.code === 'not-found' || e.toString().includes('No document to update')) {
             return false;
        }
        logFirestoreError(`updateGenerationJob (${jobId})`, e);
        return false;
    }
};


export const deleteGenerationJob = async (userId: string, jobId: string) => {
    const services = getFirebaseServices();
    if (!services) throw new Error("Firebase is not initialized. Cannot delete job.");
    try {
        await deleteDoc(doc(services.db, 'users', userId, 'generationJobs', jobId));
    } catch (e) {
        logFirestoreError('deleteGenerationJob', e);
        throw e;
    }
};

export const permanentlyDeleteAllTrashed = async (userId: string, problems: (BaseProblem | GeneratedProblem)[]) => {
    const services = getFirebaseServices();
    if (!services) throw new Error("Firebase is not initialized.");
    const { db } = services;

    const docRefsToDelete = new Map<string, DocumentReference<DocumentData>>();

    const addRef = (ref: DocumentReference<DocumentData>) => {
        if (!docRefsToDelete.has(ref.path)) {
            docRefsToDelete.set(ref.path, ref);
        }
    };

    const relatedDocPromises = problems.map(async (problem) => {
        if ('generatedParts' in problem) { // GeneratedProblem
            addRef(doc(db, 'users', userId, 'problemHistory', problem.id));
            const jobsQuery = query(collection(db, 'users', userId, 'generationJobs'), where('generatedProblemIds', 'array-contains', problem.id));
            const jobsSnapshot = await getDocs(jobsQuery);
            jobsSnapshot.forEach(jobDoc => addRef(jobDoc.ref));
        } else { // BaseProblem
            addRef(doc(db, 'users', userId, 'baseProblemHistory', problem.id));
            
            const genProblemsQuery = query(collection(db, 'users', userId, 'problemHistory'), where('baseProblemId', '==', problem.id));
            const genProblemsSnapshot = await getDocs(genProblemsQuery);
            genProblemsSnapshot.forEach(genDoc => addRef(genDoc.ref));
            
            const jobsQuery = query(collection(db, 'users', userId, 'generationJobs'), where('baseProblemId', '==', problem.id));
            const jobsSnapshot = await getDocs(jobsQuery);
            jobsSnapshot.forEach(jobDoc => addRef(jobDoc.ref));
        }
    });

    await Promise.all(relatedDocPromises);

    const allRefs = Array.from(docRefsToDelete.values());
    if (allRefs.length === 0) return;

    try {
        const BATCH_SIZE = 500;
        const batchPromises = [];
        for (let i = 0; i < allRefs.length; i += BATCH_SIZE) {
            const chunk = allRefs.slice(i, i + BATCH_SIZE);
            const batch = writeBatch(db);
            chunk.forEach(ref => batch.delete(ref));
            batchPromises.push(batch.commit());
        }
        await Promise.all(batchPromises);
    } catch (e) {
        logFirestoreError('permanentlyDeleteAllTrashed', e);
        throw e;
    }
};

// --- Worksheet Functions ---
// Stored in users/{userId}/worksheets
export const subscribeToWorksheets = (userId: string | undefined, callback: (worksheets: Worksheet[]) => void): (() => void) => {
    if (!userId) {
        callback([]);
        return () => {};
    }
    const worksheetsCollectionRef = getUserCollectionRef(userId, 'worksheets');
    if (!worksheetsCollectionRef) {
        callback([]);
        return () => {};
    }
    const q = query(worksheetsCollectionRef, orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const worksheets: Worksheet[] = [];
        querySnapshot.forEach((doc) => {
            worksheets.push({ ...(doc.data() as Omit<Worksheet, 'id'>), id: doc.id });
        });
        callback(worksheets);
    }, (error: any) => {
        console.error("Error subscribing to worksheets:", error);
        callback([]);
    });
    return unsubscribe;
};

export const addWorksheet = async (userId: string, worksheet: Omit<Worksheet, 'id'>): Promise<string | null> => {
    const worksheetsCollectionRef = getUserCollectionRef(userId, 'worksheets');
    if (!worksheetsCollectionRef) {
        throw new Error("Firestore가 초기화되지 않았습니다. 학습지를 저장할 수 없습니다.");
    }
    try {
        const docRef = await addDoc(worksheetsCollectionRef, worksheet);
        return docRef.id;
    } catch (e) {
        logFirestoreError('addWorksheet', e);
        throw e;
    }
};

export const updateWorksheet = async (userId: string, worksheetId: string, data: Partial<Omit<Worksheet, 'id'>>) => {
    const services = getFirebaseServices();
    if (!services) return;
    try {
        await updateDoc(doc(services.db, 'users', userId, 'worksheets', worksheetId), data);
    } catch (e) {
        logFirestoreError('updateWorksheet', e);
    }
};

export const deleteWorksheet = async (userId: string, worksheetId: string) => {
    const services = getFirebaseServices();
    if (!services) return;
    try {
        await deleteDoc(doc(services.db, 'users', userId, 'worksheets', worksheetId));
    } catch (e) {
        logFirestoreError('deleteWorksheet', e);
    }
};